import { getResolvedTenantCode } from 'config/resolvedTenant';
import { STORAGE_KEYS } from 'constants/storageKeys';

const memoryByTenant = new Map<string, string>();

const resolveKey = (tenantCode?: string | null): string => {
  const code = (tenantCode ?? getResolvedTenantCode() ?? '').trim().toLowerCase();
  return code || '__default__';
};

const storageKey = (tenantCode?: string | null): string =>
  `${STORAGE_KEYS.REFRESH_TOKEN}__${resolveKey(tenantCode)}`;

const readSession = (tenantCode?: string | null): string | null => {
  try {
    return sessionStorage.getItem(storageKey(tenantCode));
  } catch {
    return null;
  }
};

const writeSession = (token: string | null, tenantCode?: string | null): void => {
  try {
    const key = storageKey(tenantCode);
    if (!token) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, token);
  } catch {
    // ignore storage access errors (private mode, etc.)
  }
};

/** Removes legacy refresh tokens persisted in localStorage (XSS mitigation migration). */
export const clearPersistedRefreshTokens = (): void => {
  try {
    const legacy = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (legacy) {
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    }
    const prefix = `${STORAGE_KEYS.REFRESH_TOKEN}__`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore storage access errors
  }
};

/**
 * Refresh token store: memory + sessionStorage (sobrevive F5; se pierde al cerrar la pestaña).
 * No usa localStorage a propósito (mitigación XSS). Cookie httpOnly requiere CORS con credenciales.
 */
export const sessionRefreshStore = {
  get: (tenantCode?: string | null): string | null => {
    const key = resolveKey(tenantCode);
    const fromMemory = memoryByTenant.get(key);
    if (fromMemory) return fromMemory;
    const fromSession = readSession(tenantCode);
    if (fromSession) {
      memoryByTenant.set(key, fromSession);
      return fromSession;
    }
    return null;
  },

  set: (refreshToken: string, tenantCode?: string | null): void => {
    const trimmed = refreshToken?.trim();
    if (!trimmed) {
      sessionRefreshStore.clear(tenantCode);
      return;
    }
    const key = resolveKey(tenantCode);
    memoryByTenant.set(key, trimmed);
    writeSession(trimmed, tenantCode);
  },

  clear: (tenantCode?: string | null): void => {
    memoryByTenant.delete(resolveKey(tenantCode));
    writeSession(null, tenantCode);
  },

  clearAll: (): void => {
    memoryByTenant.clear();
    try {
      const prefix = `${STORAGE_KEYS.REFRESH_TOKEN}__`;
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(prefix)) keysToRemove.push(key);
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // ignore
    }
  },
};
